/**
 * PlacesAutocomplete — Iter166
 *
 * Wraps a text input with Google Places Autocomplete (cities only).
 * Reuses the loader from ProspectMiniMap so the JS API stays a single
 * script include per page.
 */
import { useEffect, useRef } from "react";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
              || process.env.REACT_APP_GOOGLE_MAPS_KEY
              || "";

let mapsPromise = null;
function loadMaps() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
  if (!MAPS_KEY) return Promise.reject(new Error("no-key"));
  if (mapsPromise) return mapsPromise;
  mapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&v=weekly&libraries=places`;
    s.async = true; s.defer = true;
    s.onload = () => resolve(window.google.maps);
    s.onerror = () => reject(new Error("Maps script failed"));
    document.head.appendChild(s);
  });
  return mapsPromise;
}

/**
 * @param {object} props
 * @param {string} props.value           Current city text (controlled)
 * @param {(city: string, meta?: {state?: string, lat?: number, lng?: number}) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {string} [props.testid]
 * @param {string} [props.className]
 */
export default function PlacesAutocomplete({ value, onChange, placeholder, testid, className,
                                            restrictCountries, bboxBias }) {
  const inputRef = useRef(null);
  const acRef = useRef(null);

  useEffect(() => {
    if (!inputRef.current) return;
    let cancelled = false;
    loadMaps().then((maps) => {
      if (cancelled) return;
      const opts = {
        types: ["(cities)"],
        componentRestrictions: restrictCountries ? { country: restrictCountries } : { country: "us" },
        fields: ["address_components", "geometry", "name"],
      };
      // Iter167 — bias with state bbox so "Dallas" resolves inside TX first.
      if (bboxBias && bboxBias.length === 4) {
        const [s, w, n, e] = bboxBias;
        opts.bounds = new maps.LatLngBounds({ lat: s, lng: w }, { lat: n, lng: e });
        opts.strictBounds = false;
      }
      if (acRef.current) {
        // Reconfigure existing autocomplete instead of rebuilding — Google
        // Places supports setBounds + setOptions on the fly.
        try {
          if (opts.bounds) acRef.current.setBounds(opts.bounds);
        } catch { /* ignore */ }
        return;
      }
      const ac = new maps.places.Autocomplete(inputRef.current, opts);
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        const cityName = place?.name || inputRef.current.value || "";
        let stateShort;
        for (const comp of (place?.address_components || [])) {
          if (comp.types?.includes("administrative_area_level_1")) {
            stateShort = comp.short_name;
          }
        }
        onChange(cityName, {
          state: stateShort,
          lat: place?.geometry?.location?.lat?.(),
          lng: place?.geometry?.location?.lng?.(),
        });
      });
      acRef.current = ac;
    }).catch(() => { /* silent — falls back to plain input */ });
    return () => { cancelled = true; };
  }, [restrictCountries, onChange, bboxBias]);

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className={className}
      autoComplete="off"
    />
  );
}
